import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Menu, X, ShoppingCart, User, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { useUser } from '../context/UserContext';

const FALLBACK_LOGO = '/logo.png';
const FALLBACK_DARK_LOGO = '/logo-dark.png';

const Navbar = () => {
  const { t, i18n  } = useTranslation();
  const { getCartItemsCount } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language?.substring(0, 2) || 'en';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.shop'), path: '/shop' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const cartCount = getCartItemsCount();
  const { settings } = useSettings();
  const { user, isAuthenticated } = useUser();
  const logoSrc = settings.logo_url || FALLBACK_LOGO;
  const darkLogoSrc = settings.logo_url || FALLBACK_DARK_LOGO;
  const logoAlt = lang === 'ar' && settings.site_name_ar ? settings.site_name_ar : settings.site_name || 'Itronix Electronics';

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      setIsSearchOpen(false);
      return;
    }
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setIsSearchOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2 dark:bg-[#171717]' : 'bg-white/95 backdrop-blur-md py-3 dark:bg-[#171717]/95'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src={logoSrc}
              alt={logoAlt}
              className="h-10 sm:h-12 w-auto min-w-[120px] flex-shrink-0 object-contain transform transition-transform group-hover:scale-105 dark:hidden"
            />
            <img
              src={darkLogoSrc}
              alt={logoAlt}
              className="hidden h-10 sm:h-12 w-auto min-w-[120px] flex-shrink-0 object-contain transform transition-transform group-hover:scale-105 dark:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-semibold transition-colors relative group ${
                  location.pathname === link.path
                    ? 'text-red-600'
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-red-600 transition-all group-hover:w-full ${
                    location.pathname === link.path ? 'w-full' : ''
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            <ThemeToggle />
            {isSearchOpen ? (
              <form onSubmit={handleSearchSubmit} className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('products.search')}
                  className="h-9 pl-9 pr-9"
                  autoFocus
                  onBlur={() => { if (!searchQuery.trim()) setIsSearchOpen(false); }}
                  data-testid="header-search-input"
                />
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => { setSearchQuery(''); setIsSearchOpen(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  aria-label="Close search"
                >
                  <X className="h-4 w-4" />
                </button>
              </form>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-700 hover:text-red-600"
                onClick={() => setIsSearchOpen(true)}
                aria-label={t('products.search')}
                data-testid="header-search-btn"
              >
                <Search className="h-5 w-5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="text-gray-700 hover:text-red-600 relative" asChild>
              <Link to="/cart">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
            </Button>
            {isAuthenticated ? (
              <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white" asChild>
                <Link to="/account"><User className="h-4 w-4 mr-2" />{user?.name?.split(' ')[0]}</Link>
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white" asChild>
                  <Link to="/login">{t('nav.login')}</Link>
                </Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" asChild>
                  <Link to="/register">{t('nav.register')}</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 hover:text-red-600"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t pt-4 animate-in slide-in-from-top">
            <div className="flex flex-col space-y-4">
              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('products.search')}
                  className="pl-9"
                  data-testid="mobile-header-search-input"
                />
              </form>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-semibold transition-colors ${
                    location.pathname === link.path
                      ? 'text-red-600'
                      : 'text-gray-700 hover:text-red-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/cart" className="text-sm font-semibold text-gray-700 hover:text-red-600 flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
                <ShoppingCart className="h-4 w-4 mr-2" />{t('nav.cart')} {cartCount > 0 && `(${cartCount})`}
              </Link>
              <div className="flex items-center justify-between">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="text-sm font-semibold text-red-600 flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
                    <User className="h-4 w-4 mr-2" />{user?.name}
                  </Link>
                </>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white flex-1" asChild>
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.login')}</Link>
                  </Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white flex-1" asChild>
                    <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.register')}</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
